<svelte:options customElement="dst-useractions" />

<script lang="ts">
	import type { UserData } from '@/global.d.ts';

	import { baseUrls } from '@/config.ts';
	import * as Auth from '@/lib/authentication.ts';
	import { changeState, register as registerClosables } from '@/lib/closable.ts';
	import * as events from '@/lib/events.ts';
	import { Logger } from '@/lib/log.ts';
	import { trackUserAction } from '@/lib/tracking.ts';
	import { removeNewNotificationsIndicator } from '@/lib/utils.ts';
	import notifications from '@/stores/notification_store.ts';
	import * as userStore from '@/stores/userState.ts';
	import {
		dstAbo,
		dstHeaderAccount,
		dstHeaderAccountAbo,
		dstHeaderAccountSupporter,
		dstHeaderKey,
		dstSupporterThickColor,
	} from '@derstandard/styling-toolkit/src/svg/lib.js';
	import { pushToDataLayer } from '@derstandard/tracking';
	import { createMutationObserver } from '@derstandard/utils';
	import { mdiNewspaper } from '@mdi/js';
	import { afterUpdate, onMount } from 'svelte';

	let hasMounted = false;
	let root, userActionsFlyout;
	let sessionData;
	let userState: UserData = {
		isLoggedIn: false,
	};

	const log = Logger('header.DstUseractions');

	const baseUrl =
		window.DERSTANDARD?.pageConfig?.edition === 'de' ? baseUrls.editorialweb_de : baseUrls.editorialweb_at;
	export let appid;
	export let hasnotifications;

	let profileUrls = {
		followees: `${baseUrls.legacy_profile}/postings`,
		postings: `${baseUrls.legacy_profile}/postings`,
		settings: `${baseUrls.legacy_profile}/einstellungen`,
	};

	$: {
		if (root && hasnotifications && $notifications?.hasNewNotifications) {
			log.debug("user has new notifications. adding 'notifications-active' class.");
			(root.getRootNode() as ShadowRoot).host.classList.add('notifications-active');
		} else if (root && hasnotifications) {
			log.debug("user does not have new notifications. removing 'notifications-active' class.");
			(root.getRootNode() as ShadowRoot).host.classList.remove('notifications-active');
		}
	}
	$: ariaExpanded = false;

	const { disconnect, observe } = createMutationObserver({
		onChanged: (mutation) => {
			const target = mutation.target as Element;

			if (!target.getAttribute('id') === 'app-useractions') {
				return;
			}

			const attrName = mutation.attributeName;
			const newValue = target.getAttribute(attrName);

			ariaExpanded = newValue === 'true';
		},
	});

	onMount(async () => {
		log.debug('onMount');
		log.debug('getting cognito session');
		let session;

		try {
			session = await Auth.getSession();
			sessionData = session || {};
		} catch (err) {
			log.warn(`Auth not available ${err}`);

			return;
		}

		if (session?.idToken?.payload?.mId) {
			// notifications only exist for users with a communityMemberId
			void (await import('../DstCounter/DstCounter.webcomponent.svelte')).default;
		}

		updateProfileLinks();

		if (session) {
			log.debug('session exists. getting user data.');
			userStore.set(await userStore.get());
		} else {
			log.debug("no active session. user not logged in. setting userstate in userstore to 'not logged in'.");
			userStore.set({ isLoggedIn: false });
		}

		hasMounted = true;
	});

	afterUpdate(async () => {
		registerClosables(root);
	});

	// This is neccessary to handle auth events from auth.js (for instance in forum.js).
	window.addEventListener('message', async (e) => {
		if (e.data?.eventName === events.dst.dom.overlayFinished) {
			log.debug('Update login status after overaly finished.');
			sessionData = await Auth.getSession();

			if (sessionData) {
				userStore.set(await userStore.get());
			}
		}
	});

	Auth.onStateChanged(async (e) => {
		sessionData = e;
		await updateProfileLinks();

		userStore.set(await userStore.get());
	});

	Auth.onSignedIn(async (e) => {
		sessionData = e;

		await updateProfileLinks();
	});

	log.debug('subscribing to userStore');
	userStore.store.subscribe((value) => {
		if (value) {
			log.debug('userstate changed. setting local userstate.');
			userState = value;
		} else {
			log.debug("userstate not available. setting local userstate to 'not logged in'.");
			userState = {
				isLoggedIn: false,
			};
		}
	});

	/**
	 * Click-handler for login
	 */
	function handleLogin() {
		Auth.signIn();
	}

	/**
	 * Click-handler for logout
	 */
	function handleLogout() {
		Auth.signOut();
		changeState(userActionsFlyout, false);
		pushToDataLayer({
			event: 'tracking.userAction',
			userActionType: 'registration_pl_signedout',
		});

		userStore.reset();
	}

	/**
	 * Sets profile links based on user session.
	 */
	async function updateProfileLinks() {
		const session = await Auth.getSession();
		const memberId = session?.idToken?.payload?.mId;

		profileUrls = {
			followees: memberId ? `${baseUrl}/profil/${memberId}/ich-folge` : `${baseUrls.legacy_profile}/postings`,
			postings: memberId ? `${baseUrl}/profil/${memberId}` : `${baseUrls.legacy_profile}/postings`,
			settings: memberId ? `${baseUrl}/profil/${memberId}/einstellungen` : `${baseUrls.legacy_profile}/einstellungen`,
		};
	}

	/**
	 * Click handler for the notifications tab change
	 */
	function clickNotificationsTab() {
		trackUserAction(
			$notifications.hasNewNotifications ? 'header_tabchange_notifications_new' : 'header_tabchange_notifications',
		);
		removeNewNotificationsIndicator();
	}

	/**
	 *
	 * @param node
	 */
	function mutation(node: HTMLElement) {
		observe(node);

		return {
			destroy() {
				disconnect();
			},
		};
	}
</script>

<div class="dst-useractions" bind:this={root} use:mutation>
	<!-- Wait for initial user-state -->
	{#if hasMounted}
		{#if userState?.isLoggedIn}
			<button
				aria-expanded={ariaExpanded}
				type="button"
				title="Profil"
				data-closable-target="app-useractions"
				on:click={() => trackUserAction('header_showprofile')}
			>
				<span>Profil</span>
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{#if userState.isPurUser || userState.isSmartUser || userState.hasEpaperEntitlement}
						{@html dstHeaderAccountAbo}
					{:else if userState.isSupporter}
						{@html dstHeaderAccountSupporter}
					{:else}
						{@html dstHeaderAccount}
					{/if}
				</svg>
			</button>
		{:else}
			<button type="button" title="Anmelden" on:click={handleLogin}>
				<span>Anmelden</span>
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{@html dstHeaderKey}
				</svg>
			</button>
		{/if}
	{/if}

	<dst-flyout
		id="app-useractions"
		data-closable="app-useractions"
		data-closable-active="false"
		bind:this={userActionsFlyout}
	>
		{#if hasnotifications}
			<div class="flyout--tabs" slot="tabs">
				<button type="button" disabled>Profil</button>
				<button
					type="button"
					data-closable-target="app-notifications"
					data-closable-close="true"
					on:click={clickNotificationsTab}
				>
					Mitteilungen

					<dst-counter />
				</button>
			</div>
		{/if}
		<div class="app--header--useractions--menus u-scrollbar">
			{#if appid === 'editorialweb' || appid === 'livecontent' || appid === 'userprofile'}
				<nav aria-label="Benutzer-Inhalte" class="flyout--menu">
					<a href={profileUrls.postings}>Postings</a>
					<a href={profileUrls.followees}>Mitposter:innen</a>
					<!-- TODO: Implement Push Settings -->
					<!-- <button data-closable-target="dialog-cleverpush-settings">Push-Einstellungen</button> -->
				</nav>
			{/if}
			<nav aria-label="Benutzerkonto" class="flyout--menu">
				<a
					href="{baseUrl}/profil"
					class="flyout--menu--item-complex"
					on:click={() => trackUserAction('header_showprofile')}
				>
					<small>Angemeldet als</small>
					<strong>{sessionData?.idToken?.payload?.email || ''}</strong>
				</a>

				{#if userState.isSupporter || userState.isPurUser || userState.isSmartUser}
					<a href={import.meta.env.VITE_BASE_URL_ABOSERVICE} class="flyout--menu--item-complex">
						<small>Digital-Abos und Unterstützungen verwalten</small>
						{#if userState.isSmartUser}
							<span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
									><path fill="currentColor" d={dstAbo} /></svg
								>
								<strong>DER STANDARD Smart</strong>
							</span>
						{:else if userState.isPurUser}
							<span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
									><path fill="currentColor" d={dstAbo} /></svg
								>
								<strong>derStandard.at PUR</strong>
							</span>
						{/if}
						{#if userState.isSupporter}
							<span>
								<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<!-- eslint-disable-next-line svelte/no-at-html-tags -->
									{@html dstSupporterThickColor}
								</svg>
								<strong>Aktive Unterstützung</strong>
							</span>
						{/if}
					</a>
				{/if}

				{#if userState.hasEpaperEntitlement}
					<span class="flyout--menu--item-complex">
						<small>DER STANDARD ePaper-Abo</small>
						<a href="{import.meta.env.VITE_BASE_URL_ABO}/aboservice">
							<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
								><path fill="currentColor" d={dstAbo} /></svg
							>
							<strong>Aboservice</strong>
						</a>
						<a href={baseUrls.epaper_default}>
							<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
								><path fill="currentColor" d={mdiNewspaper} /></svg
							>
							<strong>DER STANDARD ePaper lesen</strong>
						</a>
					</span>
				{/if}

				<a href={profileUrls.settings}>Einstellungen</a>
				<button type="button" title="Abmelden" on:click={handleLogout}>Abmelden</button>
			</nav>
		</div>
	</dst-flyout>
</div>

<style lang="scss" global>
	@use './DstUseractions.scss';
</style>
